// Normalizes the getWeatherCenter API response into render-ready, null-safe
// values for the cards. Everything degrades to a dash / empty list when a
// field or whole slice is missing (e.g. a data package not on the plan).

const DASH = '—';

const r = (v) =>
  v == null || v === '' || Number.isNaN(Number(v))
    ? null
    : Math.round(Number(v));
const r2 = (v) => (v == null || Number.isNaN(Number(v)) ? null : Number(v));

export const temp = (v) => (r(v) == null ? DASH : `${r(v)}°`);

// Aeris returns place/county names lowercase (e.g. "st. lawrence"). Capitalize
// each word for display → "St. Lawrence".
const titleCase = (s) =>
  s == null || s === ''
    ? s
    : String(s).replace(/\b\w/g, (ch) => ch.toUpperCase());

function uvLabel(uv) {
  if (uv == null) return '';
  if (uv <= 2) return 'Low';
  if (uv <= 5) return 'Moderate';
  if (uv <= 7) return 'High';
  if (uv <= 10) return 'Very High';
  return 'Extreme';
}
function uvColor(uv) {
  if (uv == null) return undefined;
  if (uv <= 2) return 'var(--lightning-clear)';
  if (uv <= 5) return 'var(--temp-warm)';
  if (uv <= 7) return '#FF8A65';
  return 'var(--lightning-danger)';
}

export function etaLabel(min) {
  if (min == null) return DASH;
  if (min < 60) return `~${Math.round(min)}m`;
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return m ? `~${h}h ${m}m` : `~${h}h`;
}

// Lightning threat level — uses the API status when present, else derives from
// nearest-strike distance. Returns null when there's nothing to show.
export function lightningStatus(l) {
  if (!l) return null;
  if (l.status) return l.status;
  if (l.nearestMi != null) {
    if (l.nearestMi <= 10) return 'danger';
    if (l.nearestMi <= 25) return 'caution';
    return 'clear';
  }
  if (l.count != null) return 'caution';
  return null;
}

const valOrDash = (v, suffix = '') =>
  v == null || v === '' ? DASH : `${v}${suffix}`;

export function buildViewModel(wc) {
  if (!wc) return null;

  const c = wc.conditions || null;
  const conditions = c
    ? {
        temp: temp(c.tempF),
        description: c.description || DASH,
        icon: c.icon || null,
        feelsLike:
          r(c.feelsLikeF) == null ? null : `Feels like ${r(c.feelsLikeF)}°`,
        high: temp(c.highF),
        low: temp(c.lowF),
        sunrise: c.sunrise || DASH,
        sunset: c.sunset || DASH,
        items: [
          [
            'Wind',
            c.windMph != null
              ? `${r(c.windMph)} mph${c.windDir ? ` ${c.windDir}` : ''}`
              : DASH,
          ],
          ['Gusts', c.gustMph != null ? `${r(c.gustMph)} mph` : DASH],
          ['Humidity', valOrDash(r(c.humidity), '%')],
          ['Dew Point', c.dewPointF != null ? `${r(c.dewPointF)}°` : DASH],
          ['Pressure', c.pressureIn != null ? `${r2(c.pressureIn)} in` : DASH],
          [
            'UV Index',
            c.uvIndex != null
              ? `${r(c.uvIndex)} ${uvLabel(c.uvIndex)}`.trim()
              : DASH,
            uvColor(c.uvIndex),
          ],
          [
            'Visibility',
            c.visibilityMi != null ? `${r(c.visibilityMi)} mi` : DASH,
          ],
          ['Cloud Cover', valOrDash(r(c.cloudCoverPct), '%')],
        ],
      }
    : null;

  const p = wc.precip || null;
  const precip = {
    nextRainAt: p && p.nextRainAt ? p.nextRainAt : null,
    last24: p && p.last24In != null ? r2(p.last24In).toFixed(2) : null,
    next24: p && p.next24In != null ? r2(p.next24In).toFixed(2) : null,
    rate:
      p && p.currentRateInHr != null
        ? `${r2(p.currentRateInHr).toFixed(2)} in/hr`
        : null,
  };

  const forecast = (wc.forecast || []).map((d, i) => ({
    isToday: i === 0,
    day: d.day || DASH,
    date: d.date || '',
    icon: d.icon || null,
    high: temp(d.highF),
    low: temp(d.lowF),
    precipPct: d.precipPct != null ? r(d.precipPct) : null,
    rain: d.rainIn != null ? r2(d.rainIn).toFixed(2) : '0.00',
    wind:
      d.windMph != null ? `${r(d.windMph)} ${d.windDir || ''}`.trim() : null,
    summary: d.summary || null,
    hourly: (d.hourly || []).map((h) => ({
      time: h.time || '',
      icon: h.icon || null,
      tempF: r(h.tempF),
      precipPct: r(h.precipPct),
      rainIn: r2(h.rainIn),
      windMph: r(h.windMph),
      humidity: r(h.humidity),
    })),
  }));

  // Compact 6-hour precip bars from today's hourly.
  const hourlyBars = (
    (forecast[0] && wc.forecast && wc.forecast[0] && wc.forecast[0].hourly) ||
    []
  )
    .slice(0, 6)
    .map((h) => ({ time: h.time || '', amount: r2(h.rainIn) || 0 }));

  const lightning = wc.lightning
    ? {
        status: lightningStatus(wc.lightning),
        nearest:
          wc.lightning.nearestMi != null ? r2(wc.lightning.nearestMi) : null,
        sinceLast:
          wc.lightning.sinceLastMin != null
            ? r(wc.lightning.sinceLastMin)
            : null,
        count: wc.lightning.count != null ? r(wc.lightning.count) : null,
        bearing: wc.lightning.bearing || null,
      }
    : null;

  const stormCells = (wc.stormCells || []).map((s) => ({
    type: s.type || 'Storm Cell',
    severity: s.severity || 'general',
    motion: s.motion || null,
    distanceMi: s.distanceMi != null ? r(s.distanceMi) : null,
    bearing: s.bearing || null,
    detail: [
      s.motion,
      s.distanceMi != null
        ? `${r(s.distanceMi)} mi ${s.bearing || ''}`.trim()
        : null,
    ]
      .filter(Boolean)
      .join(' — '),
    eta: etaLabel(s.etaMinutes),
    etaMinutes: s.etaMinutes != null ? r(s.etaMinutes) : null,
    maxHailIn: r2(s.maxHailIn),
    lat: r2(s.lat),
    lng: r2(s.lng),
  }));

  const alerts = (wc.alerts || []).map((a) => ({
    type: a.type || 'Weather Alert',
    county: titleCase(a.county) || null,
    issued: a.issued || null,
    effective: a.effective || null,
    expires: a.expires || null,
    body: a.body || null,
  }));

  const loc = wc.location || null;
  const cityState = loc ? [loc.city, loc.state].filter(Boolean).join(', ') : '';
  const venue = {
    name: (loc && loc.name) || null,
    // "Name — City, ST", or whichever parts exist.
    label: loc
      ? [loc.name, cityState].filter(Boolean).join(' — ') || null
      : null,
  };

  return {
    location: loc,
    venue,
    conditions,
    precip,
    hourlyBars,
    forecast,
    lightning,
    stormCells,
    alerts,
    updatedAt: wc.updatedAt || null,
  };
}
